/**
 * Member Consent API Service
 * Handles all member consent management API calls
 */

import axios from 'axios';
import { getApiBaseUrl } from '@/lib/utils';

const getHeaders = () => {
  const token = localStorage.getItem('auth_token');
  const cboId = localStorage.getItem('active_cbo_id');
  return {
    'Authorization': `Bearer ${token}`,
    'X-CBO-ID': cboId,
  };
};

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface ConsentCategory {
  id: number;
  category_key: string;
  display_name: string;
  description: string | null;
  category_group: 'enrichment' | 'communication' | 'data_sharing';
  requires_explicit_consent: boolean;
  legal_basis: string;
  is_active: boolean;
  sort_order: number;
}

export interface MemberConsent {
  id: number;
  member_id: number;
  cbo_id: number;
  category_key: string;
  category_name: string;
  consent_status: 'pending' | 'granted' | 'denied' | 'withdrawn' | 'expired';
  consent_method: string;
  granted_at: string | null;
  expires_at: string | null;
  withdrawn_at: string | null;
  collected_by_name: string | null;
  created_at: string;
}

export interface ConsentAuditEntry {
  id: number;
  category_key: string;
  action: string;
  performed_by_name: string | null;
  reason: string | null;
  created_at: string;
}

export interface ConsentSummary {
  total_members: number;
  by_group: Record<string, { consented: number; pending: number; withdrawn: number }>;
  expiring_soon: number;
}

export interface ConsentCheckResult {
  category_key: string;
  has_consent: boolean;
  consent_status: string | null;
  granted_at: string | null;
}

// ============================================================================
// API FUNCTIONS
// ============================================================================

const API = () => `${getApiBaseUrl()}/api/member-consent`;

export const memberConsentAPI = {
  // Get all consent categories
  async getCategories(): Promise<ConsentCategory[]> {
    const res = await axios.get(`${API()}/categories`, { headers: getHeaders() });
    return res.data?.categories ?? res.data;
  },

  // Get consents for a specific member
  async getMemberConsents(memberId: number): Promise<MemberConsent[]> {
    const res = await axios.get(`${API()}/member/${memberId}`, { headers: getHeaders() });
    return res.data?.consents ?? res.data;
  },

  // Grant consent for specific categories
  async grantConsent(memberId: number, categoryKeys: string[], method: string, witnessNotes?: string): Promise<any> {
    const res = await axios.post(`${API()}/grant`, {
      member_id: memberId,
      category_keys: categoryKeys,
      consent_method: method,
      witness_notes: witnessNotes,
    }, { headers: getHeaders() });
    return res.data;
  },

  // Withdraw consent for specific categories
  async withdrawConsent(memberId: number, categoryKeys: string[], reason?: string): Promise<any> {
    const res = await axios.post(`${API()}/withdraw`, {
      member_id: memberId,
      category_keys: categoryKeys,
      reason,
    }, { headers: getHeaders() });
    return res.data;
  },

  // Bulk grant or withdraw all consents
  async bulkConsent(memberId: number, grantAll: boolean, method: string, witnessNotes?: string): Promise<any> {
    const res = await axios.post(`${API()}/bulk`, {
      member_id: memberId,
      grant_all: grantAll,
      consent_method: method,
      witness_notes: witnessNotes,
    }, { headers: getHeaders() });
    return res.data;
  },

  // Check consent status for specific categories
  async checkConsent(memberId: number, categoryKeys: string[]): Promise<ConsentCheckResult[]> {
    const params = { category_keys: categoryKeys.join(',') };
    const res = await axios.get(`${API()}/check/${memberId}`, { headers: getHeaders(), params });
    return res.data?.checks ?? res.data;
  },

  // Get audit trail for a member
  async getAuditTrail(memberId: number, limit?: number, offset?: number): Promise<ConsentAuditEntry[]> {
    const params: any = {};
    if (limit !== undefined) params.limit = limit;
    if (offset !== undefined) params.offset = offset;
    const res = await axios.get(`${API()}/audit/${memberId}`, { headers: getHeaders(), params });
    return res.data?.audit_entries ?? res.data;
  },

  // Get CBO-level consent summary
  async getSummary(): Promise<ConsentSummary> {
    const res = await axios.get(`${API()}/summary`, { headers: getHeaders() });
    return res.data;
  },
};
